/* eslint-disable @next/next/no-img-element */
"use client";

import { Box, Button, Stack, Typography } from "@mui/material";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { CurrencySimple, useResponsiveValue } from "../../utils/helper";

// ---------- helpers ----------
const CURRENCY = {
    USD: { sign: "$", rate: 1, type: "string" },
    AED: { sign: "images/bannerImage/1758795455186.svg", rate: 3.67, type: "image" },
    // EUR: { sign: "€", rate: 0.92 },
};

export default function PricingCard({
    icon = '/img/personal-chauffeur/icon.svg',
    plan,
    price,
    per = "month",
    billingNote = "Billed annually.",
    features,
    ctaText = "Get started",
    hasActiveSubscription = false,
    onClick,
    highlighted = false,
    validityTo,
    bgSrc = "/img/personal-chauffeur/pricing_card.svg",
    subtitle, currency, actionType
}) {
    const responsiveFontSize = useResponsiveValue({ xs: 36, sm: 36, lg: 36 });
    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                maxWidth: { xs: 340, sm: 360, md: 380 },
                minWidth: 0,
                height: "100%",
                minHeight: 578,
                borderRadius: "20px",
                overflow: "hidden",

                // ✅ Background image setup
                backgroundImage: `url("${bgSrc}")`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center",

                // Keep same size for highlighted vs normal
                boxShadow: highlighted
                    ? "inset 0 0 0 2px #3B82F6, 0 12px 24px rgba(0,0,0,.35)"
                    : "inset 0 0 0 1px rgba(255,255,255,0.08), 0 12px 24px rgba(0,0,0,.35)",

                // Optional bottom darkening to match mock
                "&::after": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    background:
                        "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,.35) 72%, rgba(0,0,0,.55) 100%)",
                    pointerEvents: "none",
                },
            }}
        >
            <Stack
                alignItems="center"
                sx={{
                    height: "100%",
                    px: 3,
                    py: 4,
                    color: "#fff",
                    position: "relative",
                    zIndex: 1,
                }}
                spacing={3}
            >
                {/* top icon chip */}
                <Box
                    sx={{
                        width: 58,
                        height: 58,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        background:
                            "linear-gradient(180deg, rgba(255,255,255,.95) 0%, rgba(255,255,255,.85) 100%)",
                        border: "1px solid rgba(0,0,0,0.08)",
                        boxShadow: "0 6px 18px rgba(0,0,0,.25), inset 0 1px 0 rgba(255,255,255,.5)",
                    }}
                >
                    {icon ? (
                        <img src={icon} width={28} height={28} alt="" />
                    ) : (
                        <Box
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: 1,
                                background: "linear-gradient(180deg, #EAF7F8 0%, #D4EEF1 100%)",
                                border: "1px solid rgba(0,0,0,.1)",
                            }}
                        />
                    )}
                </Box>

                {/* plan name */}
                <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.95, letterSpacing: 0.2, color: "white", fontSize: "20px" }}>
                    {plan}
                </Typography>

                {/* plan subtitle */}
                <Typography variant="subtitle" sx={{ color: "#d9d9d9", textAlign: "center", marginTop: "4px !important" }}>
                    {subtitle}
                </Typography>

                {/* price */}
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.25, mt: "16px !important" }}>
                    <Typography component="span" sx={{ fontSize: 36, fontWeight: 800, lineHeight: 1 }}>
                        {CurrencySimple(price, "#fff", responsiveFontSize, CURRENCY[currency]?.type, CURRENCY[currency]?.type === "string" ? CURRENCY[currency]?.sign : null)}
                        {/* {price} */}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: 14, fontWeight: 800, opacity: 0.9 }}>
                        {`/${per}`}
                    </Typography>
                </Box>

                {/* billing note */}
                <Typography sx={{ fontSize: 16, fontWeight: 500, opacity: 0.8, mt: "12px !important" }}>{`Billed ${billingNote}`}</Typography>

                {/* features */}
                <Stack component="ul" spacing={1.2} sx={{ m: 0, p: 0, width: "100%", listStyle: "none" }}>
                    {features.map((f, idx) => (
                        <Stack key={idx} direction="row" alignItems="flex-start" spacing={1.25} component="li">
                            <CheckCircleOutlineIcon sx={{ fontSize: 20, }} />
                            <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: 1.6, opacity: 0.95 }}>{f}</Typography>
                        </Stack>
                    ))}
                </Stack>

                <Box sx={{ flex: 1 }} />


                <Button
                    onClick={(actionType === 1 || actionType === 3) ? undefined : onClick}
                    variant="contained"
                    sx={{
                        width: "86%",
                        height: 58,
                        mb: 1,
                        borderRadius: 999,
                        background: "#FFFFFF",
                        color: "#0E0F12",
                        fontWeight: 700,
                        textTransform: "none",
                        fontSize: 14,
                        boxShadow:
                            "0 10px 24px rgba(0,0,0,.35), inset 0 0 0 1px rgba(0,0,0,.06)",
                        cursor: actionType === 1 || actionType === 3 ? "default" : "pointer",
                        pointerEvents: actionType === 1 || actionType === 3 ? "none" : "auto",
                        opacity: actionType === 3 ? 0.5 : 1,
                        "&:hover": {
                            background: "#FFFFFF",
                            boxShadow: "0 10px 24px rgba(0,0,0,.35)",
                        },
                    }}
                >
                    {ctaText}
                    <br />
                    {validityTo}
                </Button>


            </Stack>
        </Box>
    );
}
